import React from 'react';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import Text from '@ff/ui/Text';
import Image from '@ff/ui/Image';
import Icon from '@ff/ui/Icon';
import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict'
import type { Props } from '.';
import Asset from './Asset';
import Link from '@ff/ui/Link';

export default function MobileMessage(props: Props) {
  const {
    darkMode,
    asset,
    url,
    title,
    text,
    avatar,
    user,
    timestamp,
    platform,
    imageAlt,
  } = props;

  const timeSinceMessageDate = `${formatDistanceToNowStrict(new Date(timestamp.date))} ago`;
  const socialLogoFontSize = platform === 'ff-icon-logo' ? '11px' : '15px';

  return (
    <Container darkMode={darkMode}>
      <Link href={url} target="_blank" rel="noreferrer noopener">
        <Asset src={asset} imageAlt={imageAlt} />
        <TextContainer>
          <MessageDateText darkMode={darkMode}>{timeSinceMessageDate}</MessageDateText>
          <TitleText darkMode={darkMode}>{title}</TitleText>
          <BodyText darkMode={darkMode}>{text}</BodyText>
          <MetadataLayout>
            <Avatar src={avatar} alt='User Avatar' />
            <MessageMetadataContainer>
              <UserNameText darkMode={darkMode}>{user}</UserNameText>
            </MessageMetadataContainer>
            <SocialLogo
              darkMode={darkMode}
              name={platform}
              fontSize={socialLogoFontSize}
            />
          </MetadataLayout>
        </TextContainer>
      </Link>
    </Container>
  );
}

interface DarkModeProps {
  darkMode?: boolean;
}

const Container = styled(Box)<DarkModeProps>(({ darkMode }) => ({
  '@media(min-width: 600px)': {
    display: 'none'
  },
  backgroundColor: darkMode ? '#393C41' : 'white',
}));

const TextContainer = styled(Box)({
  padding: '8px'
});

const MetadataLayout = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: '15px'
});

const MessageMetadataContainer = styled(Box)({
  display: 'flex',
  marginLeft: '4px',
  flexGrow: 1
})

const TitleText = styled(Text)<DarkModeProps>(({ darkMode }) => ({
  marginTop: '8px',
  fontWeight: '400',
  fontSize: '14px',
  lineHeight: '22px',
  color: darkMode ? 'white' : '#171A20',
  // Limit number of text lines
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
  lineClamp: '1',
  boxOrient: 'vertical',
  overflow: 'hidden',
}));
TitleText.defaultProps = { as: 'p' };

const BodyText = styled(Text)<DarkModeProps>(({ darkMode }) => ({
  marginTop: '3px',
  fontWeight: '300',
  fontSize: '13px',
  lineHeight: '21px',
  color: darkMode ? '#CCCFD3' : '#5C5E62',
  // Limit number of text lines
  display: '-webkit-box',
  WebkitLineClamp: '2',
  WebkitBoxOrient: 'vertical',
  lineClamp: '2',
  boxOrient: 'vertical',
  overflow: 'hidden',
}));
BodyText.defaultProps = { as: 'p' };

const UserNameText = styled(Text)<DarkModeProps>(({ darkMode }) => ({
  fontWeight: '400',
  fontSize: '12px',
  lineHeight: '14px',
  color: darkMode ? 'white' : '#171A20',
  // Limit number of text lines
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
  lineClamp: '1',
  boxOrient: 'vertical',
  overflow: 'hidden',
}));
UserNameText.defaultProps = { as: 'span' };

const MessageDateText = styled(Text)<DarkModeProps>(({ darkMode }) => ({
  width: '100%',
  textAlign: 'right',
  fontWeight: '300',
  fontSize: '11px',
  lineHeight: '13px',
  color: darkMode ? '#CCCFD3' : '#5C5E62',
  opacity: '0.7',
}));

const Avatar = styled(Image)({
  display: 'inline',
  height: '20px',
  width: '20px',
  // Circular
  borderRadius: '20px'
});

const SocialLogo = styled(Icon)<DarkModeProps>(({ darkMode }) => ({
  color: darkMode ? 'white' : '#171A20',
}));
